import { useChat, type UseChatHelpers } from '@ai-sdk/react';
import { EMPTY_ARRAY } from '@talos/kyoko';
import { DefaultChatTransport, type ChatOnToolCallCallback } from 'ai';
import { useCallback, useEffect, useMemo, useRef, useState, type PropsWithChildren } from 'react';

import { useAvaAIClientTools } from '@src/avaAI/clientTools/useAvaAIClientTools';
import { AVA_AI_COMPOSER_INPUT_ID } from '@src/components/AvaAI/tokens';
import { useFeatureFlag } from '@src/hooks/useFeatureFlag';
import { useIsMobileLayout } from '@src/hooks/useIsMobileLayout';
import {
  isAvaAIEnabled,
  lastAvaAIAssistantStepIsComplete,
  normalizeAvaAIEndpoint,
  trimIncompleteAvaAIAssistantStep,
} from '@src/utils/avaAI';
import { envConfig } from '@src/utils/envConfig';
import {
  AvaAIContext,
  AvaAIVisibilityContext,
  type AvaAIContextProps,
  type AvaAIUIMessage,
  type AvaAIVisibilityContextProps,
} from './AvaAIContext';
import { useAvaAIPowerSearchChatTurn } from './useAvaAIPowerSearchChatTurn';

const noop = () => {};
const noopAsync = async () => {};

/**
 * Served when no endpoint is configured, so the chat state machine and its transport never mount for
 * deployments without Ava-AI.
 */
const UNAVAILABLE_AVA_AI_VISIBILITY: AvaAIVisibilityContextProps = {
  isEnabled: false,
  isOpen: false,
  isPopped: false,
  open: noop,
  close: noop,
  popout: noop,
  dockBack: noop,
  registerPopoutWindow: noop,
  focusPopout: noop,
  focusComposer: noop,
  openAndFocusComposer: noop,
};

const UNAVAILABLE_AVA_AI: AvaAIContextProps = {
  ...UNAVAILABLE_AVA_AI_VISIBILITY,
  messages: EMPTY_ARRAY,
  status: 'ready',
  error: undefined,
  composerDraft: '',
  setComposerDraft: noop,
  reset: noop,
  sendMessage: noopAsync,
  sendPowerSearchMessage: noopAsync,
  cancelPowerSearchTurn: noop,
  abandonTurn: noop,
  preserveActiveTurn: noop,
  powerSearchMessageId: null,
  regenerate: noopAsync,
  stop: noopAsync,
  clearError: noop,
  composerFocusRequest: 0,
};

export function AvaAIProvider({ children }: PropsWithChildren) {
  const { enableAvaAI } = useFeatureFlag();
  const isMobileLayout = useIsMobileLayout();
  const [isOpen, setIsOpen] = useState(false);
  const [isPopped, setIsPopped] = useState(false);
  const endpoint = normalizeAvaAIEndpoint(envConfig.VITE_AVA_AI_ENDPOINT);
  const isEnabled = isAvaAIEnabled({ endpoint, enableAvaAI, isMobileLayout });

  if (endpoint === '') {
    return (
      <AvaAIVisibilityContext.Provider value={UNAVAILABLE_AVA_AI_VISIBILITY}>
        <AvaAIContext.Provider value={UNAVAILABLE_AVA_AI}>{children}</AvaAIContext.Provider>
      </AvaAIVisibilityContext.Provider>
    );
  }

  return (
    <AvaAIChatStateProvider
      endpoint={endpoint}
      isEnabled={isEnabled}
      isOpen={isOpen}
      isPopped={isPopped}
      setIsOpen={setIsOpen}
      setIsPopped={setIsPopped}
    >
      {children}
    </AvaAIChatStateProvider>
  );
}

function AvaAIChatStateProvider({
  children,
  endpoint,
  isEnabled,
  isOpen,
  isPopped,
  setIsOpen,
  setIsPopped,
}: PropsWithChildren<{
  endpoint: string;
  isEnabled: boolean;
  isOpen: boolean;
  isPopped: boolean;
  setIsOpen: (isOpen: boolean) => void;
  setIsPopped: (isPopped: boolean) => void;
}>) {
  const [composerDraft, setComposerDraft] = useState('');
  const [composerFocusRequest, setComposerFocusRequest] = useState(0);

  const focusComposer = useCallback(() => {
    setComposerFocusRequest(request => request + 1);
  }, []);

  const clientTools = useAvaAIClientTools();
  const addToolOutputRef = useRef<UseChatHelpers<AvaAIUIMessage>['addToolOutput']>();
  const turnGenerationRef = useRef(0);

  const onToolCall = useCallback<ChatOnToolCallCallback<AvaAIUIMessage>>(
    ({ toolCall }) => {
      const addToolOutput = addToolOutputRef.current;
      if (addToolOutput == null) {
        throw new Error('Ava-AI tool output handler is not initialized.');
      }
      const turnGeneration = turnGenerationRef.current;

      // Keep onToolCall pending until the client tool finishes execution. addToolOutput queues
      // another serialized update job, so only that call must be detached to avoid a deadlock.
      return clientTools.handleToolCall(toolCall, output => {
        if (turnGeneration === turnGenerationRef.current) {
          void addToolOutput(output);
        }
      });
    },
    [clientTools]
  );

  const transport = useMemo(
    () =>
      new DefaultChatTransport<AvaAIUIMessage>({
        api: `${endpoint}/api/chat`,
        credentials: 'include',
        prepareSendMessagesRequest: ({ messages }) => ({
          body: { messages, supportedClientTools: clientTools.supportedToolNames },
        }),
      }),
    [clientTools.supportedToolNames, endpoint]
  );

  const {
    messages,
    status,
    error,
    sendMessage: sendChatMessage,
    regenerate,
    stop,
    setMessages,
    clearError,
    addToolOutput,
  } = useChat<AvaAIUIMessage>({
    transport,
    onToolCall,
    sendAutomaticallyWhen: ({ messages }) => lastAvaAIAssistantStepIsComplete(messages),
  });
  addToolOutputRef.current = addToolOutput;

  // Bumping the turn discards client tool output that lands after the turn it belongs to: the SDK
  // would otherwise rewrite an unrelated message or auto-send a request the user just cancelled.
  const startNewTurn = useCallback(() => {
    turnGenerationRef.current += 1;
    clientTools.startUserTurn();
  }, [clientTools]);

  const stopMessage = useCallback(() => {
    turnGenerationRef.current += 1;
    return stop();
  }, [stop]);

  const { powerSearchMessageId, sendPowerSearchMessage, cancelPowerSearchTurn, abandonTurn, preserveActiveTurn } =
    useAvaAIPowerSearchChatTurn({
      messages,
      status,
      sendChatMessage,
      setMessages,
      startNewTurn,
      stop,
    });

  const close = useCallback(() => {
    setIsOpen(false);
    setIsPopped(false);
  }, [setIsOpen, setIsPopped]);
  const open = useCallback(() => {
    if (!isEnabled) {
      return;
    }
    setIsOpen(true);
  }, [isEnabled, setIsOpen]);
  const popout = useCallback(() => {
    if (!isEnabled) {
      return;
    }
    setIsOpen(true);
    setIsPopped(true);
  }, [isEnabled, setIsOpen, setIsPopped]);

  /** Called when the popout window is closed without the user explicitly closing the chat. */
  const dockBack = useCallback(() => {
    setIsPopped(false);
    // isOpen remains true so the docked drawer re-appears.
  }, [setIsPopped]);

  const popoutWindowRef = useRef<Window | null>(null);
  const registerPopoutWindow = useCallback((popoutWindow: Window | null) => {
    popoutWindowRef.current = popoutWindow;
  }, []);

  const focusPopout = useCallback(() => {
    const popoutWindow = popoutWindowRef.current;
    if (popoutWindow == null || popoutWindow.closed) {
      return;
    }
    popoutWindow.focus();
    popoutWindow.document.getElementById(AVA_AI_COMPOSER_INPUT_ID)?.focus();
  }, []);

  const openAndFocusComposer = useCallback(() => {
    if (isPopped) {
      focusPopout();
    } else {
      open();
    }
    focusComposer();
  }, [focusComposer, focusPopout, isPopped, open]);

  const reset = useCallback(() => {
    startNewTurn();
    void stop();
    setMessages([]);
    clearError();
    setComposerDraft('');
  }, [clearError, setComposerDraft, setMessages, startNewTurn, stop]);

  const sendMessage = useCallback(
    (text: string) => {
      startNewTurn();
      setComposerDraft('');
      return sendChatMessage({ text });
    },
    [sendChatMessage, startNewTurn]
  );

  const regenerateMessage = useCallback(() => {
    if (lastAvaAIAssistantStepIsComplete(messages)) {
      return sendChatMessage();
    }

    // Preserve completed tool/approval output by retrying without the partial step that followed it.
    const retryMessages = trimIncompleteAvaAIAssistantStep(messages);
    if (retryMessages == null) {
      return regenerate();
    }
    setMessages(retryMessages);

    return sendChatMessage();
  }, [messages, regenerate, sendChatMessage, setMessages]);

  useEffect(() => {
    if (!isEnabled) {
      void stopMessage();
      setIsOpen(false);
      setIsPopped(false);
      setComposerDraft('');
    }
  }, [isEnabled, setIsOpen, setIsPopped, stopMessage]);

  const visibilityValue = useMemo<AvaAIVisibilityContextProps>(
    () => ({
      isEnabled,
      isOpen,
      isPopped,
      open,
      close,
      popout,
      dockBack,
      registerPopoutWindow,
      focusPopout,
      focusComposer,
      openAndFocusComposer,
    }),
    [
      isEnabled,
      isOpen,
      isPopped,
      open,
      close,
      popout,
      dockBack,
      registerPopoutWindow,
      focusPopout,
      focusComposer,
      openAndFocusComposer,
    ]
  );

  const value = useMemo<AvaAIContextProps>(
    () => ({
      ...visibilityValue,
      messages,
      status,
      error,
      composerDraft,
      setComposerDraft,
      reset,
      sendMessage,
      sendPowerSearchMessage,
      cancelPowerSearchTurn,
      abandonTurn,
      preserveActiveTurn,
      powerSearchMessageId,
      regenerate: regenerateMessage,
      stop: stopMessage,
      clearError,
      composerFocusRequest,
    }),
    [
      clearError,
      composerDraft,
      composerFocusRequest,
      error,
      messages,
      regenerateMessage,
      cancelPowerSearchTurn,
      abandonTurn,
      preserveActiveTurn,
      powerSearchMessageId,
      reset,
      sendMessage,
      sendPowerSearchMessage,
      setComposerDraft,
      status,
      stopMessage,
      visibilityValue,
    ]
  );

  return (
    <AvaAIVisibilityContext.Provider value={visibilityValue}>
      <AvaAIContext.Provider value={value}>{children}</AvaAIContext.Provider>
    </AvaAIVisibilityContext.Provider>
  );
}
